import type { PaymentMode } from '@repo/desktop-bridge';
import { z } from 'zod';

/**
 * How a till takes money, and the one rule that is not symmetrical about it.
 *
 * **One copy, because four screens now name them** — the mode buttons on the
 * sale, the mode buttons at the gate, the split on the shift count, and the
 * day's list. `docs/ui-conventions.md` asks that a rule used twice live in one
 * place, and a second copy of these is a screen that says "Card" where another
 * says "CARD" and nothing catches it. The same document asks it of validation
 * in particular: a rule used by two forms is a schema fragment both import,
 * never re-typed.
 *
 * The order is the counter's rather than the contract's: cash first because it
 * is most of what a garden gate takes, and because the cash line is the one that
 * gets counted against a drawer.
 */

/**
 * What each mode reads as: the words a customer would use, not the enum.
 *
 * `Record` rather than a lookup with a default, so exhaustiveness is the type
 * system's problem: a fourth mode fails to compile here, which is the moment to
 * decide what it is called and where its button goes. That the sale's mode and
 * the ticket's are one set is asserted in the bridge (`payment.ts`), so these
 * buttons can serve both counters without either restating the other.
 */
const MODE_LABELS: Record<PaymentMode, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
};

/** The modes, in the order a counter uses them. Labels from the map above. */
export const PAYMENT_MODES: readonly { mode: PaymentMode; label: string }[] = (
  ['CASH', 'UPI', 'CARD'] as const
).map((mode) => ({ mode, label: MODE_LABELS[mode] }));

/** What one mode reads as. Total, because {@link MODE_LABELS} is exhaustive. */
export const paymentModeLabel = (mode: PaymentMode): string => MODE_LABELS[mode];

/**
 * The ceiling on a reference, from the document — `maxLength: 64` on both
 * `RecordSaleDto` and `IssueTicketDto`.
 *
 * A bound restated from the document rather than a request or response shape, so
 * there is nothing in `@repo/api-client` to import for it; named once here so the
 * two forms cannot disagree about it.
 */
export const PAYMENT_REFERENCE_MAX = 64;

/**
 * Whether this mode may carry a reference at all.
 *
 * **Named positively, and only UPI and card answer yes.** A fourth mode would
 * arrive as "carries no reference" rather than as "carries one by default",
 * which is the safe direction: an unexpected reference is refused outright by
 * the API, and a missing one never is.
 */
export const takesPaymentReference = (mode: PaymentMode | undefined): boolean =>
  mode === 'UPI' || mode === 'CARD';

/**
 * The mode field, as a schema fragment.
 *
 * **No default and no empty option**, deliberately: `undefined` until somebody
 * chooses, so the resolver refuses an unanswered mode rather than a wrong one
 * being recorded because nobody looked at it. Cash is the commonest and
 * defaulting to it would save a tap on most sales — but the mode is what Day
 * Close reconciles the drawer against, and a card sale recorded as cash is a
 * drawer short by exactly that amount with nothing on the screen having looked
 * wrong at the time.
 */
export const paymentModeField = z.enum(['CASH', 'UPI', 'CARD'], {
  message: 'Choose how they paid.',
});

/**
 * The reference field, as a schema fragment.
 *
 * Shape only. Which modes may carry one is {@link cashCarriesNoReference},
 * because that rule is about the pair rather than about this field, and a
 * refinement on the object is where the resolver can see both.
 */
export const paymentReferenceField = z
  .string()
  .trim()
  .max(PAYMENT_REFERENCE_MAX, `A reference is at most ${String(PAYMENT_REFERENCE_MAX)} characters.`)
  .optional();

/**
 * **The asymmetry, as a predicate both forms refine on.**
 *
 * A reference is permitted on UPI and card and refused outright on cash —
 * `SALES_PAYMENT_REFERENCE_NOT_ALLOWED` and
 * `TICKETING_PAYMENT_REFERENCE_NOT_ALLOWED`, both 422 and both non-retryable, so
 * a till must correct it rather than replay it. The API keeps the rule in one
 * place for both routes (`common/payment/payment-reference.ts`) precisely so a
 * till would not enforce it two ways; this is that one place on this side.
 *
 * The *message* is each form's own — a sale and a ticket are different objects
 * to a person — so only the rule lives here.
 */
export const cashCarriesNoReference = (values: {
  paymentMode?: PaymentMode;
  paymentReference?: string;
}): boolean =>
  values.paymentMode !== 'CASH' ||
  values.paymentReference === undefined ||
  values.paymentReference === '';

/**
 * The reference as it goes on the wire, or nothing at all.
 *
 * **`''` is not "no reference" and must never be sent.** Both DTOs carry
 * `minLength: 1`, so an empty string is refused where an absent key is accepted
 * — and `''` is exactly what a form produces when somebody types into the field
 * and then clears it. Omitting the key and sending `null` are the same thing;
 * an empty string is neither.
 *
 * Cash returns nothing whatever was typed, so a stale reference cannot ride
 * along on a mode switch the screen failed to clear after.
 */
export const paymentReferenceOnWire = (
  mode: PaymentMode,
  reference: string | undefined,
): string | undefined => {
  if (!takesPaymentReference(mode)) return undefined;

  const trimmed = reference?.trim() ?? '';

  return trimmed === '' ? undefined : trimmed;
};
